import React, { useEffect, useState } from 'react';

import { SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { Button, Tabs, Tooltip } from 'antd';

import { HotjarEvents, submitEvent } from 'hotjar';
import BlacklistMessage from 'v2/components/Shared/BlacklistMessage';
import PlgAnticipation from 'v2/components/Shared/PlgAnticipation';
import TopPage from 'v2/components/Shared/TopPage';
import Tour from 'v2/components/Shared/Tour';
import { ToggleButton } from 'v2/components/Shared/ValuesVisibility';
import { sendEventToGA } from 'v2/helpers/analytics';
import { EFinancialStatementTabs } from 'v2/interfaces/financial';
import type { ListOrder } from 'v2/services/financialStatement/statement';

import Blocked from './Blocked';
import CardList from './CardList';
import { tourSteps } from './constants';
import ExportModal from './ExportModal';
import Filter from './Filter';
import Future from './Future';
import Statement from './Statement';

const Tag = () => (
  <div className='bg-grey-200 mb-2 rounded-sm px-[10px] py-[5px] text-xs uppercase'>
    Wallet <strong>BRL</strong>
  </div>
);

function FinancialStatement() {
  const [shouldDisabledSortBtn, setShouldDisabledSortBtn] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<ListOrder>('DESC');
  const [tab, setTab] = useState<EFinancialStatementTabs>(EFinancialStatementTabs.STATEMENT);
  const [exportModalItsOpen, setExportModalItsOpen] = useState(false);

  const showOrderButton = tab !== EFinancialStatementTabs.STATEMENT;
  const currOrderIsAscending = currentOrder === 'ASC';

  const handleChange = (newTab: string) => {
    const translateTabName = (t: EFinancialStatementTabs) => {
      if (t === EFinancialStatementTabs.STATEMENT) return 'Extrato';
      if (t === EFinancialStatementTabs.FUTURE) return 'Lançamentos Futuros';
      return 'Lançamentos Bloqueados';
    };

    sendEventToGA({
      category: 'Novo Extrato',
      action: 'SubAba',
      label: translateTabName(newTab as EFinancialStatementTabs)
    });

    setTab(newTab as EFinancialStatementTabs);
  };

  const handleClickExport = () => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Botão',
      label: 'Exportar'
    });

    setExportModalItsOpen(true);
  };

  const handleToggleExportModal = () => setExportModalItsOpen(prevState => !prevState);
  const handleChangeOrder = () => setCurrentOrder(currOrderIsAscending ? 'DESC' : 'ASC');

  const AdditionalButtons = () => (
    <>
      {showOrderButton && (
        <Tooltip title={`Ordem ${currOrderIsAscending ? '' : 'de'}crescente`} placement='top'>
          <Button
            className='mr-2! min-w-[40px] p-0 disabled:opacity-30'
            type='default'
            onClick={handleChangeOrder}
            disabled={shouldDisabledSortBtn}
          >
            {currOrderIsAscending ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
          </Button>
        </Tooltip>
      )}

      <Tooltip title='Exporte e analise seu extrato em períodos de até 12 meses' placement='top'>
        <Button type='default' onClick={handleClickExport}>
          Exportar
        </Button>
      </Tooltip>
    </>
  );

  useEffect(() => submitEvent(HotjarEvents.NEW_STATEMENT_ACCESS), []);

  return (
    <div className='font-sans'>
      <BlacklistMessage />

      <TopPage
        title='Extrato Financeiro'
        extra={<Tag />}
        additionalContent={<ToggleButton />}
        helpLink='https://ajuda.eduzz.com/hc/pt-br/articles/4402964669467-Extrato-Financeiro-%C3%93rbita'
      />

      <CardList />
      <Filter currentTab={tab} />
      <div id='main-results' className='mt-4'>
        <Tabs
          className='mt-5 mb-6 font-semibold'
          activeKey={tab}
          onChange={handleChange}
          tabBarExtraContent={{
            right: (
              <div className='flex max-sm:hidden'>
                <AdditionalButtons />
              </div>
            )
          }}
          items={[
            { key: EFinancialStatementTabs.STATEMENT, label: 'Extrato' },
            { key: EFinancialStatementTabs.FUTURE, label: 'Lançamentos Futuros' },
            { key: EFinancialStatementTabs.BLOCKED, label: 'Lançamentos Bloqueados' }
          ]}
        />

        <div className='hidden max-sm:mx-2 max-sm:mb-7 max-sm:flex'>
          <AdditionalButtons />
        </div>

        {tab === EFinancialStatementTabs.STATEMENT && <Statement />}
        {tab === EFinancialStatementTabs.FUTURE && (
          <Future order={currentOrder} shouldDisableSortBtn={setShouldDisabledSortBtn} />
        )}
        {tab === EFinancialStatementTabs.BLOCKED && (
          <Blocked order={currentOrder} shouldDisableSortBtn={setShouldDisabledSortBtn} />
        )}

        {exportModalItsOpen && <ExportModal isOpen={exportModalItsOpen} setClose={handleToggleExportModal} />}

        <Tour steps={tourSteps} name='financial_statement_tour' />
      </div>

      <PlgAnticipation page='financial-statement' />
    </div>
  );
}

export default FinancialStatement;
